import React, { useState, useRef, useEffect, useCallback, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Building2,
  BarChart3,
  FileText,
  Link2,
  Tag,
  Loader2,
} from 'lucide-react';
import { useOmniSearch, OmniSearchResult, OmniSearchResultType } from '../../hooks/useOmniSearch';
import { useEntitlements } from '../../hooks/useEntitlements';
import Eyebrow from './Eyebrow';

// ─── Icon + label config per entity type ────────────────────────────────────
// Icons render quiet (ink-500) with no colored tile; the highlighted row
// carries the only accent (navy).

const TYPE_CONFIG: Record<
  OmniSearchResultType,
  { label: string; Icon: React.FC<{ className?: string; strokeWidth?: number }> }
> = {
  issuer: { label: 'Issuers', Icon: Building2 },
  portfolio: { label: 'Portfolios', Icon: BarChart3 },
  claim: { label: 'Claims', Icon: FileText },
  evidence: { label: 'Evidence', Icon: Link2 },
  characteristic: { label: 'Characteristics', Icon: Tag },
};

// ─── Sub-components ──────────────────────────────────────────────────────────

interface ResultItemProps {
  result: OmniSearchResult;
  isHighlighted: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}

const ResultItem: React.FC<ResultItemProps> = ({ result, isHighlighted, onMouseEnter, onClick }) => {
  const { Icon } = TYPE_CONFIG[result.type];
  return (
    <button
      type="button"
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
        isHighlighted ? 'bg-paper-2' : 'hover:bg-paper-2'
      }`}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      <span className="flex-shrink-0 w-[22px] h-[22px] flex items-center justify-center">
        <Icon className={`w-4 h-4 ${isHighlighted ? 'text-navy-700' : 'text-ink-500'}`} strokeWidth={1.25} />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block font-serif text-[15px] truncate ${isHighlighted ? 'text-navy-700' : 'text-ink-900'}`}>
          {result.title}
        </span>
        {result.subtitle && (
          <span className="block text-xs text-ink-500 truncate">{result.subtitle}</span>
        )}
      </span>
    </button>
  );
};

interface ResultGroupProps {
  type: OmniSearchResultType;
  results: OmniSearchResult[];
  highlightedIndex: number;
  groupStartIndex: number;
  onHover: (idx: number) => void;
  onSelect: (result: OmniSearchResult) => void;
}

const ResultGroup: React.FC<ResultGroupProps> = ({
  type,
  results,
  highlightedIndex,
  groupStartIndex,
  onHover,
  onSelect,
}) => {
  if (results.length === 0) return null;
  const { label } = TYPE_CONFIG[type];
  return (
    <div>
      <div className="px-4 py-1.5 bg-paper-2 border-b border-stone-200">
        <Eyebrow>{label}</Eyebrow>
      </div>
      {results.map((result, i) => (
        <ResultItem
          key={result.id}
          result={result}
          isHighlighted={highlightedIndex === groupStartIndex + i}
          onMouseEnter={() => onHover(groupStartIndex + i)}
          onClick={() => onSelect(result)}
        />
      ))}
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

interface OmniSearchBarProps {
  inputRef?: React.RefObject<HTMLInputElement | null>;
  placeholder?: string;
  className?: string;
  variant?: 'hero' | 'compact';
}

const OmniSearchBar: React.FC<OmniSearchBarProps> = ({
  inputRef: externalRef,
  placeholder,
  className = '',
  variant = 'hero',
}) => {
  const isCompact = variant === 'compact';
  const resolvedPlaceholder = placeholder ?? (isCompact ? 'Search…' : 'Search by name, ticker, LEI, CIK…');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const internalRef = useRef<HTMLInputElement | null>(null);
  const inputRef = externalRef ?? internalRef;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { results, loading, totalCount } = useOmniSearch(query);
  const { canReadEvidence } = useEntitlements();

  // Filter out evidence results when user lacks read_evidence permission
  const visibleEvidence = canReadEvidence ? results.evidence : [];

  // Flatten results into a single ordered list for keyboard nav
  const flatResults: OmniSearchResult[] = [
    ...results.issuers,
    ...results.portfolios,
    ...results.claims,
    ...visibleEvidence,
    ...results.characteristics,
  ];

  // Group start indices for the ResultGroup component
  const issuerStart = 0;
  const portfolioStart = results.issuers.length;
  const claimStart = portfolioStart + results.portfolios.length;
  const evidenceStart = claimStart + results.claims.length;
  const characteristicStart = evidenceStart + visibleEvidence.length;

  const handleSelect = useCallback(
    (result: OmniSearchResult) => {
      setQuery('');
      setOpen(false);
      setHighlightedIndex(-1);
      navigate(result.url);
    },
    [navigate]
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % flatResults.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev - 1 + flatResults.length) % flatResults.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && flatResults[highlightedIndex]) {
          handleSelect(flatResults[highlightedIndex]);
        }
        break;
      case 'Escape':
        setOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputRef]);

  // Open dropdown when results arrive or query changes. Adjusted during
  // render (React's documented pattern for syncing state to a changed
  // value) rather than in an effect, to avoid a cascading-render setState.
  const openSyncKey = `${query}:${totalCount}`;
  const [prevOpenSyncKey, setPrevOpenSyncKey] = useState(openSyncKey);
  if (openSyncKey !== prevOpenSyncKey) {
    setPrevOpenSyncKey(openSyncKey);
    if (query.trim().length >= 2) {
      setOpen(true);
      setHighlightedIndex(-1);
    } else {
      setOpen(false);
    }
  }

  // Groups arrive one by one, so show whatever has landed while the rest load.
  const showEmpty = !loading && open && query.trim().length >= 2 && totalCount === 0;
  const showResults = open && totalCount > 0;
  const showLoading = loading && open && totalCount === 0;

  return (
    <div className={`relative ${isCompact ? '' : 'w-full'} ${className}`}>
      {/* Input */}
      <div className="relative">
        <Search strokeWidth={1.25} className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-ink-400 ${
          isCompact ? 'left-3 w-4 h-4' : 'left-4 w-5 h-5'
        }`} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (query.trim().length >= 2) setOpen(true);
          }}
          placeholder={resolvedPlaceholder}
          autoComplete="off"
          spellCheck={false}
          className={isCompact
            ? 'w-[200px] focus:w-[320px] pl-9 pr-8 py-1.5 text-sm rounded-lg border border-border bg-muted/50 focus:bg-background focus:outline-none focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f]/20 transition-all duration-200'
            : 'w-full pl-12 pr-12 py-4 text-base rounded-2xl border border-stone-200 bg-white shadow-md focus:outline-none focus:border-[#1e3a5f] focus:shadow-lg transition-all'
          }
          style={isCompact ? undefined : { fontSize: '16px' }}
          aria-label="Omni search"
          aria-autocomplete="list"
          aria-expanded={open}
        />
        {loading && (
          <Loader2 className={`absolute top-1/2 -translate-y-1/2 text-ink-400 animate-spin ${
            isCompact ? 'right-2.5 w-4 h-4' : 'right-4 w-5 h-5'
          }`} />
        )}
        {isCompact && !loading && (
          <kbd className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-0.5 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            <span className="text-xs">⌘</span>K
          </kbd>
        )}
      </div>

      {/* Dropdown panel */}
      {open && (
        <div
          ref={dropdownRef}
          className={`absolute left-0 top-full mt-2 z-50 bg-white border border-stone-200 shadow-xl overflow-hidden ${
            isCompact ? 'w-[380px] rounded-lg' : 'right-0 rounded-2xl'
          }`}
          role="listbox"
        >
          {showLoading && (
            <div className="px-4 py-6 text-center text-sm text-ink-400">Searching…</div>
          )}

          {showEmpty && (
            <div className="px-4 py-6 text-center text-sm text-ink-400">
              No results found for <span className="font-medium text-ink-700">"{query}"</span>
            </div>
          )}

          {showResults && (
            <div className="max-h-96 overflow-y-auto divide-y divide-stone-200">
              <ResultGroup
                type="issuer"
                results={results.issuers}
                highlightedIndex={highlightedIndex}
                groupStartIndex={issuerStart}
                onHover={setHighlightedIndex}
                onSelect={handleSelect}
              />
              <ResultGroup
                type="portfolio"
                results={results.portfolios}
                highlightedIndex={highlightedIndex}
                groupStartIndex={portfolioStart}
                onHover={setHighlightedIndex}
                onSelect={handleSelect}
              />
              <ResultGroup
                type="claim"
                results={results.claims}
                highlightedIndex={highlightedIndex}
                groupStartIndex={claimStart}
                onHover={setHighlightedIndex}
                onSelect={handleSelect}
              />
              {canReadEvidence && (
                <ResultGroup
                  type="evidence"
                  results={visibleEvidence}
                  highlightedIndex={highlightedIndex}
                  groupStartIndex={evidenceStart}
                  onHover={setHighlightedIndex}
                  onSelect={handleSelect}
                />
              )}
              <ResultGroup
                type="characteristic"
                results={results.characteristics}
                highlightedIndex={highlightedIndex}
                groupStartIndex={characteristicStart}
                onHover={setHighlightedIndex}
                onSelect={handleSelect}
              />
            </div>
          )}

          {/* Footer hint */}
          {showResults && (
            <div className="px-4 py-2 border-t border-stone-200 flex items-center gap-3 text-xs text-ink-400 bg-paper-2">
              <span>↑↓ navigate</span>
              <span>↵ select</span>
              <span>esc close</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OmniSearchBar;
